/* =============================================================================
 * LA VERSIÓN DE LA PLATAFORMA: QUE NADIE SE QUEDE EN UNA VIEJA SIN SABERLO
 * -----------------------------------------------------------------------------
 * (11-oct-2026) Una pestaña abierta, o la plataforma instalada en el teléfono
 * que vuelve de segundo plano, sigue corriendo el código con el que se abrió:
 * nada la recarga después de un despliegue. Esto lo resuelve así:
 *
 *   · Cada build lleva un IDENTIFICADOR (el commit; si no se sabe, la fecha y
 *     hora del build). Va dentro del código (`REACT_APP_AXIS_VERSION`, lo pone
 *     scripts/build.mjs) y en un archivo público sin caché, `/version.json`, que
 *     escribe el mismo script al terminar.
 *   · La plataforma lo consulta al abrir, al volver a primer plano y cada 15 min.
 *     Si el servido es otro, una franja dice "Hay una versión nueva…" con
 *     "Actualizar".
 *   · VERSIÓN MÍNIMA: el archivo declara también `minimo`. Si el NIVEL del código
 *     cargado es menor, la plataforma se actualiza sola —nunca con un formulario
 *     sin guardar, nunca a media checada—.
 *
 * ═══ CÓMO SE SUBE LA VERSIÓN MÍNIMA ═══════════════════════════════════════════
 * Con UN número: `NIVEL_MINIMO`, aquí abajo. Súbelo en 1 SOLO en el despliegue que
 * rompe a las versiones viejas (por ejemplo, un cambio de reglas de Firestore que
 * el código viejo ya no cumple). Ese build lleva el nivel nuevo dentro y lo
 * declara como mínimo en /version.json; todo lo que se cargó antes trae el nivel
 * anterior y se actualiza solo. En cualquier otro despliegue, NO lo toques: basta
 * la franja.
 *
 * CommonJS, exportado nombre por nombre (como src/logica/checador.js): lo usan la
 * plataforma (App.tsx) y scripts/build.mjs.
 * ========================================================================== */

// ⬇️ EL NÚMERO QUE SE SUBE (solo en despliegues que rompen versiones viejas).
// Historial: 1 (11-oct-2026, al estrenarse) · 2 (11-oct-2026, sedes y razones
// sociales: un teléfono con la versión anterior mediría la checada contra la
// ubicación general y no contra la sede del trabajador).
const NIVEL_MINIMO = 2;

const RUTA_VERSION = "/version.json";
const INTERVALO_REVISION_MS = 15 * 60 * 1000;
// Con la actualización forzada pendiente (formulario sin guardar o checada a
// medias), cada cuánto se vuelve a mirar, sin pedir nada a la red.
const INTERVALO_PENDIENTE_MS = 3000;
// Lo que se deja ver "Actualizando la plataforma…" antes de recargar.
const PAUSA_ANTES_DE_RECARGAR_MS = 900;
// Si después de recargar el navegador sigue sirviendo el código viejo (una caché
// que no suelta), no se recarga en bucle: tras este número de intentos para la
// MISMA versión servida, se deja la franja con "Actualizar".
const MAX_RECARGAS_FORZADAS = 2;
const CLAVE_RECARGAS = "axis:recargasForzadas";

const TEXTO_VERSION_NUEVA = "Hay una versión nueva de AXIS Legaltech.";
const TEXTO_BOTON_ACTUALIZAR = "Actualizar";
const TEXTO_ACTUALIZANDO = "Actualizando la plataforma…";
const TEXTO_GUARDA_PARA_ACTUALIZAR = "Guarda lo que estás capturando para actualizar";
const TEXTO_CONFIRMAR_ACTUALIZAR_CON_CAPTURA = "Lo que escribiste todavía no se ha guardado y se va a perder al actualizar. ¿Actualizar de todos modos?";

// LA VERSIÓN CON LA QUE SE CONSTRUYÓ ESTE CÓDIGO. La sustituye el build
// (`process.env.REACT_APP_AXIS_VERSION`). Sin ella —en desarrollo, en las
// pruebas— es "" y no se revisa nada: no hay contra qué comparar.
function versionCargada() {
  try { return String(process.env.REACT_APP_AXIS_VERSION || ""); } catch (e) { return ""; }
}

// EL IDENTIFICADOR DE UN BUILD: los 12 primeros caracteres del commit, o si no se
// sabe, la fecha y hora (UTC) del build. Dos builds del mismo commit son la misma
// versión: no hay nada nuevo que avisar.
function identificadorDeVersion(o) {
  const sha = String((o && o.sha) || "").trim().toLowerCase();
  if (/^[0-9a-f]{7,40}$/.test(sha)) return sha.slice(0, 12);
  const d = (o && o.ahora instanceof Date) ? o.ahora : new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `build-${d.getUTCFullYear()}${p(d.getUTCMonth() + 1)}${p(d.getUTCDate())}-${p(d.getUTCHours())}${p(d.getUTCMinutes())}${p(d.getUTCSeconds())}`;
}
// LO QUE SE ESCRIBE EN /version.json.
function contenidoDeVersionJson(o) {
  return { version: String(o.version), minimo: Number(o.minimo), generado: (o.ahora instanceof Date ? o.ahora : new Date()).toISOString() };
}
// LO QUE SE LEE DE /version.json, comprobado. Algo que no tiene forma de versión
// es null: un archivo roto no puede ni avisar ni forzar nada.
function leerVersionServida(json) {
  if (!json || typeof json !== "object") return null;
  const version = String(json.version || "").trim();
  if (!version) return null;
  const minimo = Number(json.minimo);
  return { version, minimo: Number.isFinite(minimo) ? minimo : 0 };
}

// ¿QUÉ HAY QUE HACER? Una sola función y sin efectos: la pantalla pinta lo que
// diga y recarga solo si dice `recargar`.
//   · franja: null | "nueva" | "actualizando" | "guarda"
//   · recargar: true solo si es forzosa y no hay nada que perder ni checada a medias.
// Orden de las preguntas:
//   1) sin versión cargada o sin versión servida, nada;
//   2) MÍNIMA: el nivel cargado es menor que el mínimo servido →
//        · checada a medias: se espera ("actualizando", sin recargar);
//        · captura sin guardar: "guarda", sin recargar;
//        · ya se intentó demasiadas veces con esta versión: "nueva" (botón);
//        · si no: "actualizando" y se recarga;
//   3) DISTINTA, o un SERVICE WORKER NUEVO ESPERANDO (`swEsperando`): "nueva", con
//      el botón. Un service worker nuevo ya no recarga la página por su cuenta
//      (src/index.tsx, public/sw.js): lo atiende esta misma franja;
//   4) igual: nada.
function decidirActualizacion(o) {
  const cargada = String((o && o.cargada) || "");
  const s = o && o.servida && o.servida.version ? o.servida : null;
  if (!cargada) return { franja: null, recargar: false };
  if (!s) return { franja: o.swEsperando ? "nueva" : null, recargar: false };
  const nivel = Number(o.nivelCargado);
  if (Number.isFinite(nivel) && Number(s.minimo) > nivel) {
    if (o.hayChecada) return { franja: "actualizando", recargar: false };
    if (o.hayCaptura) return { franja: "guarda", recargar: false };
    if (Number(o.recargasForzadas || 0) >= MAX_RECARGAS_FORZADAS) return { franja: "nueva", recargar: false };
    return { franja: "actualizando", recargar: true };
  }
  if (s.version !== cargada || o.swEsperando) return { franja: "nueva", recargar: false };
  return { franja: null, recargar: false };
}
function textoDeFranja(franja) {
  if (franja === "nueva") return TEXTO_VERSION_NUEVA;
  if (franja === "actualizando") return TEXTO_ACTUALIZANDO;
  if (franja === "guarda") return TEXTO_GUARDA_PARA_ACTUALIZAR;
  return "";
}

exports.NIVEL_MINIMO = NIVEL_MINIMO;
exports.RUTA_VERSION = RUTA_VERSION;
exports.INTERVALO_REVISION_MS = INTERVALO_REVISION_MS;
exports.INTERVALO_PENDIENTE_MS = INTERVALO_PENDIENTE_MS;
exports.PAUSA_ANTES_DE_RECARGAR_MS = PAUSA_ANTES_DE_RECARGAR_MS;
exports.MAX_RECARGAS_FORZADAS = MAX_RECARGAS_FORZADAS;
exports.CLAVE_RECARGAS = CLAVE_RECARGAS;
exports.TEXTO_VERSION_NUEVA = TEXTO_VERSION_NUEVA;
exports.TEXTO_BOTON_ACTUALIZAR = TEXTO_BOTON_ACTUALIZAR;
exports.TEXTO_ACTUALIZANDO = TEXTO_ACTUALIZANDO;
exports.TEXTO_GUARDA_PARA_ACTUALIZAR = TEXTO_GUARDA_PARA_ACTUALIZAR;
exports.TEXTO_CONFIRMAR_ACTUALIZAR_CON_CAPTURA = TEXTO_CONFIRMAR_ACTUALIZAR_CON_CAPTURA;
exports.versionCargada = versionCargada;
exports.identificadorDeVersion = identificadorDeVersion;
exports.contenidoDeVersionJson = contenidoDeVersionJson;
exports.leerVersionServida = leerVersionServida;
exports.decidirActualizacion = decidirActualizacion;
exports.textoDeFranja = textoDeFranja;
